import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { toHttps } from './imageUtils';

/**
 * Bulk Upload Helper for scraped images
 * This utility helps upload products with scraped images
 */

// Category mapping - maps image prefix to category details
export const CATEGORY_MAPPING = {
  'silk-saree': {
    categoryName: 'Silk Saree',
    categoryType: 'Saree',
    description: 'Premium silk sarees with traditional designs',
    tags: ['silk', 'saree', 'traditional', 'premium', 'wedding']
  },
  'cotton-saree': {
    categoryName: 'Cotton Saree',
    categoryType: 'Saree',
    description: 'Comfortable cotton sarees for daily wear',
    tags: ['cotton', 'saree', 'casual', 'comfortable', 'daily-wear']
  },
  'georgette-saree': {
    categoryName: 'Georgette Saree',
    categoryType: 'Saree',
    description: 'Elegant georgette sarees for special occasions',
    tags: ['georgette', 'saree', 'elegant', 'party-wear', 'festive']
  },
  'net-saree': {
    categoryName: 'Net Saree',
    categoryType: 'Saree',
    description: 'Beautiful net sarees with intricate designs',
    tags: ['net', 'saree', 'designer', 'party-wear', 'embroidered']
  },
  'bridal-lehenga': {
    categoryName: 'Bridal Lehenga',
    categoryType: 'Lehenga',
    description: 'Stunning bridal lehengas for your special day',
    tags: ['bridal', 'lehenga', 'wedding', 'designer', 'premium']
  }
};

/**
 * Convert File to base64
 */
export const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Upload single image to server
 */
export const uploadImage = async (base64String, fileName) => {
  try {
    // Convert base64 to file
    const byteString = atob(base64String.split(',')[1]);
    const mimeString = base64String.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);

    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([ab], { type: mimeString });
    const file = new File([blob], fileName, { type: mimeString });

    const formData = new FormData();
    formData.append('file', file);

    const config = {
      method: 'post',
      maxBodyLength: Infinity,
      url: '/upload',
      baseURL: API_BASE_URL,
      headers: {
        'Authorization': 'QuindlTokPATFileUpload2025#$$TerOiu$',
        'Content-Type': 'multipart/form-data',
      },
      data: formData,
    };

    const response = await axios.request(config);
    return toHttps(response.data.filePath);
  } catch (error) {
    console.error('Image upload failed:', error);
    throw error;
  }
};

/**
 * Upload multiple images
 */
export const uploadImages = async (base64Images, onProgress) => {
  const urls = [];
  for (let i = 0; i < base64Images.length; i++) {
    const fileName = `product-${Date.now()}-${i}.jpg`;
    const url = await uploadImage(base64Images[i], fileName);
    urls.push(url);
    if (onProgress) {
      onProgress(i + 1, base64Images.length);
    }
  }
  return urls;
};

/**
 * Group images by category prefix
 */
export const groupImagesByCategory = (files) => {
  const grouped = {};

  files.forEach(file => {
    const fileName = file.name;
    // Extract category from filename (e.g., "silk-saree-1767413059678-0.jpg" -> "silk-saree")
    const match = fileName.match(/^([a-z-]+)-\d+-\d+\.jpg$/);

    if (match) {
      const categoryPrefix = match[1];
      if (!grouped[categoryPrefix]) {
        grouped[categoryPrefix] = [];
      }
      grouped[categoryPrefix].push(file);
    }
  });

  return grouped;
};

/**
 * Group images by timestamp to create product variants
 * Images with same timestamp belong to same product
 */
export const groupImagesByProduct = (files) => {
  const grouped = {};

  files.forEach(file => {
    const fileName = file.name;
    // Extract category and timestamp (e.g., "silk-saree-1767413059678-0.jpg")
    const match = fileName.match(/^([a-z-]+)-(\d+)-\d+\.jpg$/);

    if (match) {
      const categoryPrefix = match[1];
      const timestamp = match[2];
      const key = `${categoryPrefix}-${timestamp}`;

      if (!grouped[key]) {
        grouped[key] = {
          category: categoryPrefix,
          timestamp,
          images: []
        };
      }
      grouped[key].images.push(file);
    }
  });

  return Object.values(grouped);
};

/**
 * Generate product code
 */
export const generateProductCode = (categoryPrefix, index) => {
  const prefix = categoryPrefix.toUpperCase().replace(/-/g, '_');
  return `${prefix}_${String(index).padStart(4, '0')}`;
};

/**
 * Generate product name based on category
 */
export const generateProductName = (categoryPrefix, index) => {
  const categoryInfo = CATEGORY_MAPPING[categoryPrefix];
  if (!categoryInfo) return `Product ${index}`;

  const names = {
    'silk-saree': ['Kanjivaram Silk Saree', 'Banarasi Silk Saree', 'Mysore Silk Saree', 'Tussar Silk Saree'],
    'cotton-saree': ['Handloom Cotton Saree', 'Printed Cotton Saree', 'Tant Cotton Saree', 'Mangalgiri Cotton Saree'],
    'georgette-saree': ['Embroidered Georgette Saree', 'Printed Georgette Saree', 'Designer Georgette Saree'],
    'net-saree': ['Embroidered Net Saree', 'Stone Work Net Saree', 'Designer Net Saree', 'Party Wear Net Saree'],
    'bridal-lehenga': ['Designer Bridal Lehenga', 'Heavy Embroidered Lehenga', 'Royal Bridal Lehenga', 'Wedding Lehenga']
  };

  const nameList = names[categoryPrefix] || [`${categoryInfo.categoryName}`];
  return nameList[index % nameList.length];
};

/**
 * Generate variant names
 */
export const generateVariantName = (categoryPrefix, index) => {
  const colors = ['Red', 'Blue', 'Green', 'Pink', 'Yellow', 'Orange', 'Purple', 'Maroon', 'Navy', 'Teal'];
  return colors[index % colors.length];
};

/**
 * Generate product description
 */
export const generateDescription = (categoryPrefix, productName) => {
  const categoryInfo = CATEGORY_MAPPING[categoryPrefix];

  const descriptions = {
    'silk-saree': 'Exquisite silk saree featuring traditional craftsmanship and elegant design. Perfect for weddings, festivals, and special occasions.',
    'cotton-saree': 'Comfortable and breathable cotton saree ideal for daily wear. Features beautiful prints and traditional patterns.',
    'georgette-saree': 'Elegant georgette saree with modern designs and intricate detailing. Perfect for parties and festive celebrations.',
    'net-saree': 'Beautiful net saree with delicate embroidery and stone work. Ideal for special occasions and evening events.',
    'bridal-lehenga': 'Stunning bridal lehenga with heavy embellishments and intricate work. Designed to make your special day memorable.'
  };

  return descriptions[categoryPrefix] || `Beautiful ${productName} with premium quality fabric and elegant design.`;
};

/**
 * Create product data from grouped images
 */
export const createProductData = async (productGroup, categoryId, index, onImageUploadProgress) => {
  const { category, images } = productGroup;
  const categoryInfo = CATEGORY_MAPPING[category];

  if (!categoryInfo) {
    throw new Error(`Unknown category: ${category}`);
  }

  // Convert files to base64
  const base64Images = await Promise.all(
    images.slice(0, 6).map(file => fileToBase64(file))
  );

  // Upload images
  const imageUrls = await uploadImages(base64Images, onImageUploadProgress);

  // Generate product details
  const productName = generateProductName(category, index);
  const productCode = generateProductCode(category, index);
  const variantName = generateVariantName(category, index);
  const description = generateDescription(category, productName);

  // Determine price range based on category
  const priceRanges = {
    'silk-saree': { min: 80, max: 200 },
    'cotton-saree': { min: 30, max: 80 },
    'georgette-saree': { min: 50, max: 120 },
    'net-saree': { min: 60, max: 150 },
    'bridal-lehenga': { min: 200, max: 500 }
  };

  const range = priceRanges[category] || { min: 50, max: 150 };
  const actualPrice = Math.floor(Math.random() * (range.max - range.min) + range.min);
  const sellingPrice = Math.floor(actualPrice * 0.9); // 10% discount

  return {
    productName,
    productCode,
    categoryId,
    categoryName: categoryInfo.categoryName,
    variantName,
    description,
    actualPrice,
    sellingPrice,
    tax: 5,
    color: '#000000',
    images: imageUrls,
    tags: categoryInfo.tags,
    stock: Math.floor(Math.random() * 50) + 10,
    sizeAndFit: categoryInfo.categoryType === 'Saree' ? 'Length: 5.5 meters, Blouse: 0.8 meters' : 'FREE SIZE',
    availability: true,
    sareeSize: categoryInfo.categoryType === 'Saree' ? 5.5 : undefined,
    blouseSize: categoryInfo.categoryType === 'Saree' ? 0.8 : undefined,
    weight: 600,
    materialAndCare: 'Dry clean recommended, Store in cool dry place, Avoid direct sunlight'
  };
};

/**
 * Add product to database
 */
export const addProduct = async (productData) => {
  try {
    const response = await fetch(`${API_BASE_URL}/products/addproduct`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(productData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to add product');
    }

    return response.json();
  } catch (error) {
    console.error('Failed to add product:', error);
    throw error;
  }
};

/**
 * Main bulk upload function
 */
export const bulkUploadProducts = async (files, categoryMap, onProgress) => {
  try {
    // Group images by product (same timestamp)
    const productGroups = groupImagesByProduct(files);

    const results = {
      success: [],
      failed: [],
      total: productGroups.length
    };

    for (let i = 0; i < productGroups.length; i++) {
      const group = productGroups[i];
      const categoryPrefix = group.category;
      const categoryId = categoryMap[categoryPrefix];

      if (!categoryId) {
        results.failed.push({
          group,
          error: `Category not found for ${categoryPrefix}`
        });
        continue;
      }

      try {
        // Update progress - image upload
        if (onProgress) {
          onProgress({
            stage: 'uploading_images',
            current: i + 1,
            total: productGroups.length,
            productName: generateProductName(categoryPrefix, i)
          });
        }

        // Create product data and upload images
        const productData = await createProductData(
          group,
          categoryId,
          i,
          (current, total) => {
            if (onProgress) {
              onProgress({
                stage: 'uploading_images',
                current: i + 1,
                total: productGroups.length,
                imageProgress: { current, total }
              });
            }
          }
        );

        // Update progress - creating product
        if (onProgress) {
          onProgress({
            stage: 'creating_product',
            current: i + 1,
            total: productGroups.length,
            productName: productData.productName
          });
        }

        // Add product to database
        const result = await addProduct(productData);
        results.success.push(result);

      } catch (error) {
        console.error(`Failed to upload product ${i}:`, error);
        results.failed.push({
          group,
          error: error.message
        });
      }
    }

    return results;
  } catch (error) {
    console.error('Bulk upload failed:', error);
    throw error;
  }
};
